import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from './AuthContext'
import api from '../services/api'

/**
 * SeasonContext — état global de la saison sélectionnée.
 * Source de vérité unique pour toutes les pages saisonnalisées.
 *
 * Expose :
 *  - currentSeason       : saison effective (API /config/season/ → fallback local)
 *  - nextSeason          : saison suivante sur le calendrier réel (jamais influencée par l'override)
 *  - selectedSeason      : saison choisie par l'utilisateur (persistée localStorage)
 *  - availableSeasons    : liste des saisons existantes en base + saison à venir
 *  - seasonHistory       : { [season]: { is_prepared, club_id, club_name, category } }
 *  - isSeasonPrepared(s) : bool — la saison a-t-elle été préparée (entrée coach_club_history)
 *  - reloadSeasonStatus  : recharger l'état de préparation
 *
 * Les pages utilisent useSeason() pour lire selectedSeason et l'inclure dans leurs requêtes.
 * L'interceptor axios (services/api.js) ajoute automatiquement ?season= sur les endpoints saisonnalisés.
 */

const SeasonContext = createContext(null)

const STORAGE_KEY      = 'insightball_season'
const LAST_KNOWN_KEY   = 'last_known_effective_season'

// Calcule la saison FFF en cours (1er août → 31 juillet) — fallback synchrone
function computeCurrentSeason() {
  const now = new Date()
  const year = now.getFullYear()
  if (now.getMonth() >= 7) { // août = 7 (zero-indexed)
    return `${year}-${year + 1}`
  }
  return `${year - 1}-${year}`
}

// Calcule la saison suivante (la "saison à venir")
function computeNextSeason(current) {
  const [, end] = current.split('-').map(Number)
  return `${end}-${end + 1}`
}

export function SeasonProvider({ children }) {
  const { isAuthenticated } = useAuth()

  // currentSeason est initialisé synchroniquement (computeCurrentSeason),
  // puis mis à jour avec effective_season reçue de l'API au montage.
  const [currentSeason, setCurrentSeason] = useState(computeCurrentSeason)
  // nextSeason ancré sur le calendrier réel, jamais sur currentSeason (effective/forcée).
  // Si forced_current_season = '2026-2027' en juin 2026, nextSeason reste '2026-2027'
  // (la saison qui suit le calendrier) et non '2027-2028' (qui serait la "next" après le forcé).
  const nextSeason = computeNextSeason(computeCurrentSeason())

  // État initial : saison du localStorage si valide, sinon saison calculée localement.
  // On utilise computeCurrentSeason() directement (pas la variable state) car useState
  // n'évalue son initialiseur qu'une seule fois — avant que currentSeason (state) soit disponible.
  const [selectedSeason, setSelectedSeasonState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored || computeCurrentSeason()
  })

  // Saisons disponibles en base + saison à venir toujours présente
  const [availableSeasons, setAvailableSeasonsState] = useState(() => {
    const cs = computeCurrentSeason()
    return [computeNextSeason(cs), cs]
  })

  // Historique des préparations par saison (depuis coach_club_history)
  const [seasonHistory, setSeasonHistory] = useState({})

  // État orphelin de la saison sélectionnée
  const [orphanStatus, setOrphanStatus] = useState({ is_orphan: false, club_name: null })

  // Persiste à chaque changement
  const setSelectedSeason = (season) => {
    setSelectedSeasonState(season)
    if (season) {
      localStorage.setItem(STORAGE_KEY, season)
    }
  }

  // Wrapper setAvailableSeasons : garantit que nextSeason et currentSeason sont toujours présentes
  const setAvailableSeasons = (seasons) => {
    const arr = Array.isArray(seasons) ? [...seasons] : [currentSeason]
    if (!arr.includes(currentSeason)) arr.push(currentSeason)
    if (!arr.includes(nextSeason)) arr.push(nextSeason)
    arr.sort((a, b) => b.localeCompare(a)) // du plus récent au plus ancien
    setAvailableSeasonsState(arr)
  }

  // Helper : la saison X a-t-elle été préparée par le coach ?
  const isSeasonPrepared = useCallback((season) => {
    return Boolean(seasonHistory[season]?.is_prepared)
  }, [seasonHistory])

  // Charger l'état de préparation des saisons depuis le backend
  const reloadSeasonStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/coach/club-history/')
      const map = {}
      if (data?.history && Array.isArray(data.history)) {
        for (const h of data.history) {
          map[h.season] = {
            is_prepared: true,
            club_id: h.club_id,
            club_name: h.club_name,
            category: h.category,
            is_current: h.is_current,
          }
        }
      }
      setSeasonHistory(map)
    } catch (e) {
      // Silencieux : pas connecté ou endpoint absent
    }
  }, [])

  // Récupère effective_season depuis l'API, ajuste currentSeason et détecte
  // une selectedSeason stale. Dépend de isAuthenticated (pas deps=[]) pour
  // re-fire après login — sinon l'effet rate le token écrit par authService.
  useEffect(() => {
    if (!isAuthenticated) return

    api.get('/config/season/')
      .then(({ data }) => {
        const effective = data.effective_season
        const stored   = localStorage.getItem(STORAGE_KEY)
        let lastKnown = localStorage.getItem(LAST_KNOWN_KEY)
        if (lastKnown === null && stored) {
          // Premier passage de ce code sur ce navigateur : on n'a aucune trace
          // d'un choix explicite passé, donc on assume que `stored` était hérité
          // du défaut — peu importe que ce montage tombe avant ou après l'UPDATE
          // côté serveur.
          lastKnown = stored
        }

        if (!stored) {
          // Aucune saison en localStorage → suivre le défaut effectif
          setSelectedSeason(effective)
        } else if (lastKnown && stored === lastKnown && effective !== lastKnown) {
          // L'utilisateur était sur l'ancien défaut (stored === lastKnown) et la
          // saison effective a changé → il n'avait pas fait de choix explicite,
          // on le bascule vers la nouvelle saison effective.
          setSelectedSeason(effective)
        }
        // Sinon : stored diffère de lastKnown → choix délibéré, on ne touche pas.

        setCurrentSeason(effective)
        localStorage.setItem(LAST_KNOWN_KEY, effective)
      })
      .catch(() => {
        // Fallback : computeCurrentSeason() déjà dans le state, rien à faire.
      })
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  // Charger l'état de préparation au montage (uniquement si connecté)
  useEffect(() => {
    if (!isAuthenticated) return
    reloadSeasonStatus()
  }, [isAuthenticated, reloadSeasonStatus])

  // Charger l'état orphelin pour la saison sélectionnée (uniquement si connecté)
  useEffect(() => {
    if (!isAuthenticated) {
      setOrphanStatus({ is_orphan: false, club_name: null })
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const { data } = await api.get('/coach/orphan-status/', { params: { season: selectedSeason } })
        if (!cancelled && data) {
          setOrphanStatus({
            is_orphan: Boolean(data.is_orphan),
            club_name: data.club_name || null,
          })
        }
      } catch {
        if (!cancelled) setOrphanStatus({ is_orphan: false, club_name: null })
      }
    }
    load()
    return () => { cancelled = true }
  }, [isAuthenticated, selectedSeason])

  // Expose la saison globale via window pour l'interceptor axios
  useEffect(() => {
    window.__insightball_selected_season = selectedSeason
  }, [selectedSeason])

  return (
    <SeasonContext.Provider value={{
      currentSeason,
      nextSeason,
      selectedSeason,
      setSelectedSeason,
      availableSeasons,
      setAvailableSeasons,
      seasonHistory,
      isSeasonPrepared,
      reloadSeasonStatus,
      orphanStatus,
      isOrphan: orphanStatus.is_orphan,
      orphanClubName: orphanStatus.club_name,
      isCurrentSeason: selectedSeason === currentSeason,
      isNextSeason: selectedSeason === nextSeason,
      isPastSeason: selectedSeason < currentSeason,
    }}>
      {children}
    </SeasonContext.Provider>
  )
}

export function useSeason() {
  const ctx = useContext(SeasonContext)
  if (!ctx) {
    const cur = computeCurrentSeason()
    return {
      currentSeason: cur,
      nextSeason: computeNextSeason(cur),
      selectedSeason: cur,
      setSelectedSeason: () => {},
      availableSeasons: [cur],
      setAvailableSeasons: () => {},
      seasonHistory: {},
      isSeasonPrepared: () => false,
      reloadSeasonStatus: () => {},
      orphanStatus: { is_orphan: false, club_name: null },
      isOrphan: false,
      orphanClubName: null,
      isCurrentSeason: true,
      isNextSeason: false,
      isPastSeason: false,
    }
  }
  return ctx
}

export function getSelectedSeason() {
  return window.__insightball_selected_season || computeCurrentSeason()
}

export function formatSeasonShort(season) {
  if (!season) return ''
  const [start, end] = season.split('-')
  return `${start.slice(2)}/${end.slice(2)}`
}

export function formatSeasonLong(season) {
  if (!season) return ''
  return `Saison ${season}`
}
